import type { SalePublic } from "@/lib/offline/db";
import type { TicketContext } from "@/lib/printing";
import { WebBluetoothPrinter, type BluetoothPrinterInfo } from "./bluetooth";
import { buildEscPosPayload, buildTestTicketPayload } from "./encoder";
import { WebUsbPrinter, type UsbPrinterInfo } from "./usb";

export type PrintTransport = "usb" | "bluetooth" | "agent" | "browser";
export type PrintPreference = "auto" | "usb" | "bluetooth" | "agent" | "browser";

export interface HardwarePrinterSettings {
  preference: PrintPreference;
  autoCut: boolean;
  openDrawerOnCash: boolean;
  autoPrintOnSale: boolean;
  lastUsbDevice: { vendorId: number; productId: number; name: string } | null;
  lastBluetoothDevice: { id: string; name: string } | null;
}

const STORAGE_KEY = "megasyr.hardware.printer";

const DEFAULT_SETTINGS: HardwarePrinterSettings = {
  preference: "auto",
  autoCut: true,
  openDrawerOnCash: true,
  autoPrintOnSale: false,
  lastUsbDevice: null,
  lastBluetoothDevice: null,
};

export class PrinterHardwareManager {
  private static instance: PrinterHardwareManager;
  private usbDriver = new WebUsbPrinter();
  private btDriver = new WebBluetoothPrinter();
  private settings: HardwarePrinterSettings = DEFAULT_SETTINGS;
  private autoReconnectAttempted = false;

  private constructor() {
    this.loadSettings();
  }

  public static getInstance(): PrinterHardwareManager {
    if (!PrinterHardwareManager.instance) {
      PrinterHardwareManager.instance = new PrinterHardwareManager();
    }
    return PrinterHardwareManager.instance;
  }

  public getSettings(): HardwarePrinterSettings {
    return { ...this.settings };
  }

  public updateSettings(partial: Partial<HardwarePrinterSettings>): HardwarePrinterSettings {
    this.settings = { ...this.settings, ...partial };
    this.saveSettings();
    return this.getSettings();
  }

  private loadSettings(): void {
    if (typeof localStorage === "undefined") return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.settings = { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch {
      this.settings = { ...DEFAULT_SETTINGS };
    }
  }

  private saveSettings(): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Ignorar
    }
  }

  // --- Estado de Conexión ---

  public isUsbSupported(): boolean {
    return WebUsbPrinter.isSupported();
  }

  public isBluetoothSupported(): boolean {
    return WebBluetoothPrinter.isSupported();
  }

  public isUsbConnected(): boolean {
    return this.usbDriver.isConnected();
  }

  public isBluetoothConnected(): boolean {
    return this.btDriver.isConnected();
  }

  public getUsbInfo(): UsbPrinterInfo | null {
    return this.usbDriver.getInfo();
  }

  public getBluetoothInfo(): BluetoothPrinterInfo | null {
    return this.btDriver.getInfo();
  }

  // --- Operaciones de Conexión ---

  public async connectUsb(): Promise<UsbPrinterInfo> {
    const info = await this.usbDriver.requestAndConnect();
    this.updateSettings({
      lastUsbDevice: {
        vendorId: info.vendorId,
        productId: info.productId,
        name: info.productName,
      },
    });
    return info;
  }

  public async disconnectUsb(): Promise<void> {
    await this.usbDriver.disconnect();
    this.updateSettings({ lastUsbDevice: null });
  }

  public async connectBluetooth(): Promise<BluetoothPrinterInfo> {
    const info = await this.btDriver.requestAndConnect();
    this.updateSettings({
      lastBluetoothDevice: {
        id: info.id,
        name: info.name,
      },
    });
    return info;
  }

  public async disconnectBluetooth(): Promise<void> {
    await this.btDriver.disconnect();
    this.updateSettings({ lastBluetoothDevice: null });
  }

  /**
   * Intenta reconectar silenciosamente con dispositivos previamente autorizados.
   */
  public async ensureConnection(): Promise<void> {
    if (this.autoReconnectAttempted) return;
    this.autoReconnectAttempted = true;

    if (this.settings.lastUsbDevice && !this.usbDriver.isConnected()) {
      await this.usbDriver.autoReconnect(
        this.settings.lastUsbDevice.vendorId,
        this.settings.lastUsbDevice.productId,
      );
    }
  }

  // --- Flujo de Impresión Principal ---

  /**
   * Imprime una venta utilizando el mejor transporte disponible.
   * Devuelve el destino por el que se realizó la impresión.
   */
  public async printSale(
    sale: SalePublic,
    context: TicketContext,
  ): Promise<{ success: boolean; destination: PrintTransport }> {
    await this.ensureConnection();

    const hasCash = (sale.payments ?? []).some((p) => p.method === "efectivo");
    const openDrawer = this.settings.openDrawerOnCash && hasCash;
    const cutPaper = this.settings.autoCut;

    // 1. Si está conectado o preferido WebUSB
    if (
      (this.settings.preference === "auto" || this.settings.preference === "usb") &&
      this.isUsbConnected()
    ) {
      try {
        const payload = buildEscPosPayload(sale, context, { openDrawer, cutPaper });
        await this.usbDriver.send(payload);
        return { success: true, destination: "usb" };
      } catch (err) {
        console.warn("Fallo al imprimir por WebUSB, intentando siguiente transporte:", err);
      }
    }

    // 2. Si está conectado o preferido Web Bluetooth
    if (
      (this.settings.preference === "auto" || this.settings.preference === "bluetooth") &&
      this.isBluetoothConnected()
    ) {
      try {
        const payload = buildEscPosPayload(sale, context, { openDrawer, cutPaper });
        await this.btDriver.send(payload);
        return { success: true, destination: "bluetooth" };
      } catch (err) {
        console.warn("Fallo al imprimir por Bluetooth, intentando siguiente transporte:", err);
      }
    }

    // 3. Agente Local en localhost
    const agentUrl = import.meta.env.VITE_PRINT_AGENT_URL;
    if (
      (this.settings.preference === "auto" || this.settings.preference === "agent") &&
      agentUrl
    ) {
      try {
        const payload = buildEscPosPayload(sale, context, { openDrawer, cutPaper });
        const res = await fetch(`${agentUrl}/print-raw`, {
          method: "POST",
          headers: { "Content-Type": "application/octet-stream" },
          body: payload,
          signal: AbortSignal.timeout(1200),
        });
        if (res.ok) {
          return { success: true, destination: "agent" };
        }
      } catch {
        // Continuar a fallback navegador
      }
    }

    // 4. Fallback estándar al diálogo del navegador en iframe
    return { success: true, destination: "browser" };
  }

  /**
   * Imprime un ticket de calibración y prueba física directa hacia la impresora.
   */
  public async printTest(
    businessName: string,
    paperWidth: 58 | 80,
  ): Promise<{ success: boolean; destination: PrintTransport }> {
    await this.ensureConnection();

    const payload = buildTestTicketPayload(businessName, paperWidth, this.settings.openDrawerOnCash);

    if (this.isUsbConnected()) {
      await this.usbDriver.send(payload);
      return { success: true, destination: "usb" };
    }

    if (this.isBluetoothConnected()) {
      await this.btDriver.send(payload);
      return { success: true, destination: "bluetooth" };
    }

    return { success: false, destination: "browser" };
  }
}

export const printerManager = PrinterHardwareManager.getInstance();
